import { useTranslation } from 'react-i18next';

import { mapIntegrationProviderToDTO } from '@/modules/common/mappers/integration.mapper';
import { IntegrationServices } from '@/modules/common/types/integrations';
import { MissingIntegrationNotice } from '@/modules/onboarding/common/components/resolve-duplicates-modal/missing-integration-notice';
import { ResolveOnboardingConflictsModal } from '@/modules/onboarding/common/components/resolve-duplicates-modal/resolve-onboarding-conflicts-modal';
import {
  MergeRequestBuilder,
  OnboardingResolveConflictAttributeRow,
} from '@/modules/onboarding/common/components/resolve-duplicates-modal/types';
import { invalidateOnboardingProjectQueries } from '@/modules/onboarding/common/utils/onboarding-query-invalidation';
import { removeDuplicatesByKey } from '@/modules/onboarding/common/utils/reconciliation-helpers';

import { mergeProjects } from '../../api/projects';
import { Project } from '../../types/projects.types';
import { MergeProjectsRequest } from '../../types/request/merge-projects.request';

const PROJECTS_I18N_NAMESPACE = 'authenticated.onboarding.projects';

interface IProps {
  selectedItems: Project[];
  onSuccess: () => void;
  connectedIntegrations: IntegrationServices[];
  disabled?: boolean;
  disabledTooltip?: string;
}

function getAttributeRows(
  t: (key: string) => string
): OnboardingResolveConflictAttributeRow<Project>[] {
  return [
    {
      key: 'id',
      label: t(`${PROJECTS_I18N_NAMESPACE}:duplicates.name`),
      placeholder: t(`${PROJECTS_I18N_NAMESPACE}:duplicates.selectName`),
      getSelectOptions: items =>
        removeDuplicatesByKey(items, 'name').map(item => ({
          value: `${item.id}`,
          label: item.name,
        })),
      getDisplayValue: item => item.name,
      isEmpty: item => !item.name.trim(),
    },
    {
      key: 'client',
      label: t(`${PROJECTS_I18N_NAMESPACE}:duplicates.clientName`),
      placeholder: t(`${PROJECTS_I18N_NAMESPACE}:duplicates.selectClient`),
      getSelectOptions: items =>
        removeDuplicatesByKey(items, 'client', val => val?.id).map(item => ({
          value: `${item.client!.id}`,
          label: item.client!.name,
        })),
      getDisplayValue: item => item.client?.name ?? '',
      isEmpty: item => !item.client?.name?.trim(),
    },
  ];
}

const mergeRequestBuilder: MergeRequestBuilder<Project> = {
  build: (formData, selectedItems, integrationsToUse) => {
    const selectedProjectId = formData.id;
    const selectedProject = selectedItems.find(project => project.id === +selectedProjectId);

    if (!selectedProject) {
      return null;
    }

    const request: MergeProjectsRequest = {
      idsToMerge: selectedItems.map(item => item.id),
      resultingProject: {
        name: selectedProject.name,
        clientId: +formData.client,
        importedFromFilename: selectedProject?.importedFromFilename,
        integrationsToUse: integrationsToUse.map(i => ({
          integration: mapIntegrationProviderToDTO(i.integration as IntegrationServices),
          sourceUserId: i.sourceId,
        })),
      },
    };

    return request;
  },
};

export function ResolveOnboardingProjectsConflictsModal({
  selectedItems,
  onSuccess,
  connectedIntegrations,
  disabled,
  disabledTooltip,
}: IProps) {
  const { t } = useTranslation();

  const attributeRows = getAttributeRows(t);

  return (
    <ResolveOnboardingConflictsModal
      selectedItems={selectedItems}
      onSuccess={onSuccess}
      attributeRows={attributeRows}
      integrationsList={connectedIntegrations}
      mergeMutationFn={mergeProjects}
      invalidateQueries={invalidateOnboardingProjectQueries}
      mergeRequestBuilder={mergeRequestBuilder}
      i18nNamespace={PROJECTS_I18N_NAMESPACE}
      personLabelI18nKey={`${PROJECTS_I18N_NAMESPACE}:duplicates.person`}
      resultingEntityI18nKey={`${PROJECTS_I18N_NAMESPACE}:duplicates.resultingEntity`}
      renderFooterNotice={() => (
        <MissingIntegrationNotice i18nNamespace={PROJECTS_I18N_NAMESPACE} />
      )}
      triggerDisabled={disabled}
      triggerDisabledTooltip={disabledTooltip}
    />
  );
}
